import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type RowSelectionState,
  type Table as TanStackTable,
  type Row,
} from '@tanstack/react-table'
import { ReactNode, useRef, useEffect } from 'react'
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import Spinner from './Spinner'
import { APP_COLORS } from '../colors'

export interface TableProps<TData> {
  data: TData[]
  columns: ColumnDef<TData>[]
  topHeader?: ReactNode
  loading?: boolean
  enableRowSelection?: boolean
  rowSelection?: RowSelectionState
  onRowSelectionChange?: (selectedRows: TData[]) => void
  enableSorting?: boolean
  showSortArrows?: boolean
  sorting?: SortingState
  onSortingChange?: (sorting: SortingState) => void
  onRowClick?: (row: TData) => void
  pagination?: {
    pageIndex: number
    pageSize: number
    totalCount: number
    totalPages?: number
    onPageChange: (pageIndex: number) => void
    onPageSizeChange?: (pageSize: number) => void
    pageSizeOptions?: number[]
  }
  className?: string
}

export default function Table<TData extends { id: string | number }>({
  data,
  columns,
  topHeader,
  loading = false,
  enableRowSelection = false,
  rowSelection = {},
  onRowSelectionChange,
  enableSorting = true,
  showSortArrows = true,
  sorting = [],
  onSortingChange,
  onRowClick,
  pagination,
  className = '',
}: TableProps<TData>) {
  // Handle sorting
  const handleSortingChange = (updater: SortingState | ((old: SortingState) => SortingState)) => {
    if (!onSortingChange) return
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater
    onSortingChange(newSorting)
  }

  // Handle row selection
  const handleRowSelectionChange = (updater: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)) => {
    if (!onRowSelectionChange) return
    const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater
    
    // Get selected rows data
    const selectedRowIds = Object.keys(newSelection).filter(key => newSelection[key])
    const selectedRows = data.filter((_, index) => selectedRowIds.includes(String(index)))
    onRowSelectionChange(selectedRows)
  }

  // Add selection column if enabled
  const SelectAllCheckbox = ({ table }: { table: TanStackTable<TData> }) => {
    const checkboxRef = useRef<HTMLInputElement>(null)
    const isSomeSelected = table.getIsSomePageRowsSelected()
    
    useEffect(() => {
      if (checkboxRef.current) {
        checkboxRef.current.indeterminate = isSomeSelected
      }
    }, [isSomeSelected])

    return (
      <input
        ref={checkboxRef}
        type="checkbox"
        checked={table.getIsAllPageRowsSelected()}
        onChange={table.getToggleAllPageRowsSelectedHandler()}
        className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
      />
    )
  }

  const tableColumns = enableRowSelection
    ? [
        {
          id: 'select',
          header: ({ table }: { table: TanStackTable<TData> }) => (
            <SelectAllCheckbox table={table} />
          ),
          cell: ({ row }: { row: Row<TData> }) => (
            <input
              type="checkbox"
              checked={row.getIsSelected()}
              onChange={row.getToggleSelectedHandler()}
              className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
            />
          ),
          enableSorting: false,
        },
        ...columns,
      ]
    : columns

  const table = useReactTable({
    data,
    columns: tableColumns as ColumnDef<TData>[],
    getCoreRowModel: getCoreRowModel(),
    manualSorting: enableSorting,
    manualPagination: true,
    state: {
      sorting: enableSorting ? sorting : undefined,
      rowSelection: enableRowSelection ? rowSelection : undefined,
      pagination: pagination
        ? {
            pageIndex: pagination.pageIndex,
            pageSize: pagination.pageSize,
          }
        : {
            pageIndex: 0,
            pageSize: 10,
          },
    },
    onSortingChange: enableSorting ? handleSortingChange : undefined,
    onRowSelectionChange: enableRowSelection ? handleRowSelectionChange : undefined,
    pageCount: pagination ? Math.ceil(pagination.totalCount / pagination.pageSize) : undefined,
    enableRowSelection: enableRowSelection,
  })
 
  const defaultPageSizeOptions = [10, 20, 40, 80, 100]
  const pageSizeOptions = pagination?.pageSizeOptions ?? defaultPageSizeOptions
 
  const currentPage = pagination?.pageIndex ?? 0
  const pageSize = pagination?.pageSize ?? 10
  const totalCount = pagination?.totalCount ?? data.length
  const startItem = totalCount === 0 ? 0 : currentPage * pageSize + 1
  const endItem = Math.min((currentPage + 1) * pageSize, totalCount)
  const pageCount = pagination?.totalPages ?? (pagination ? Math.ceil(pagination.totalCount / pagination.pageSize) : Math.ceil(data.length / pageSize))

  return (
    <div className={`w-full ${className}`}> 
      {topHeader && <div className="mb-4">{topHeader}</div>}

      {/* Table Container */}
      <div className="bg-background-white rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-background border-b border-border">
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => {
                    const canSort = header.column.getCanSort() && enableSorting
                    const meta = header.column.columnDef.meta as { cellClassName?: string; headerClassName?: string } | undefined
                    const headerCellClass = meta?.headerClassName ?? meta?.cellClassName ?? ''
                    return (
                      <th
                        key={header.id}
                        className={`px-6 py-4 text-left text-xs font-semibold text-text-primary tracking-wider ${headerCellClass}`.trim()}
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            className={`flex items-center gap-2 ${
                              canSort ? 'cursor-pointer select-none hover:text-text-primary' : ''
                            }`}
                            onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {canSort && showSortArrows && (
                              <span className="flex flex-col">
                                {header.column.getIsSorted() === 'asc' ? (
                                  <ChevronUpIcon className="w-4 h-4 text-primary" />
                                ) : header.column.getIsSorted() === 'desc' ? (
                                  <ChevronDownIcon className="w-4 h-4 text-primary" />
                                ) : (
                                  <div className="flex flex-col -space-y-1">
                                    <ChevronUpIcon className="w-3 h-3 text-text-muted" />
                                    <ChevronDownIcon className="w-3 h-3 text-text-muted" />
                                  </div>
                                )}
                              </span>
                            )}
                          </div>
                        )}
                      </th>
                    )
                  })}
                </tr>
              ))}
            </thead>
            <tbody className="bg-background-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={tableColumns.length} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <Spinner size="lg" />
                      <p className="text-text-secondary">Loading...</p>
                    </div>
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={tableColumns.length} className="px-6 py-12 text-center">
                    <p className="text-text-secondary"> No data available</p>
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, index) => {
                  const isSelected = enableRowSelection && rowSelection[index]
                  return (
                    <tr
                      key={row.id}
                      className={`group transition-colors ${
                        isSelected
                          ? 'bg-primary-light hover:bg-primary-light'
                          : 'hover:bg-background'
                      } ${onRowClick ? 'cursor-pointer' : ''}`}
                      onClick={() => onRowClick?.(row.original)}
                    >
                      {row.getVisibleCells().map(cell => {
                        const meta = cell.column.columnDef.meta as { cellClassName?: string } | undefined
                        const cellClassName = meta?.cellClassName ?? 'whitespace-nowrap'
                        return (
                          <td
                            key={cell.id}
                            className={`px-6 py-5 text-sm text-text-primary ${cellClassName}`}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
 
        {(pagination || pageCount > 1) && (
          <div className="px-6 py-4 border-t border-border-light flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <span className="text-sm text-text-secondary">
                Showing {startItem} to {endItem} of {totalCount} results
              </span>
              {pagination?.onPageSizeChange && (
                <div className="flex items-center gap-2 ">
                  <label htmlFor="table-page-size" className="text-sm text-text-secondary whitespace-nowrap">
                    Rows per page
                  </label>
                  <select
                    id="table-page-size"
                    value={String(pageSize)}
                    onChange={e => {
                      const num = Number(e.target.value)
                      pagination?.onPageSizeChange?.(num)
                      pagination?.onPageChange(0)
                    }}
                    className="px-2 cursor-pointer py-1.5 text-sm text-text-primary border border-border-light rounded-lg bg-background-white focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    {pageSizeOptions.map(size => (
                      <option key={size} value={String(size)}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 ">
              <button
                onClick={() => {
                  if (pagination && currentPage > 0) {
                    pagination.onPageChange(currentPage - 1)
                  }
                }}
                disabled={currentPage === 0}
                className="p-2   cursor-pointer rounded-lg border border-border text-text-primary hover:bg-background disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Previous page"
              >
                <ChevronLeftIcon className="w-5 h-5 " />
              </button>
              <div className="flex items-center gap-1 ">
                {Array.from({ length: Math.min(5, pageCount) }, (_, i) => {
                  let pageNumber: number

                  if (pageCount <= 5) {
                    pageNumber = i + 1
                  } else if (currentPage < 3) {
                    pageNumber = i + 1
                  } else if (currentPage > pageCount - 3) {
                    pageNumber = pageCount - 4 + i
                  } else {
                    pageNumber = currentPage - 2 + i
                  }

                  return (
                    <button
                      key={pageNumber}
                      onClick={() => {
                        if (pagination) {
                          pagination.onPageChange(pageNumber - 1)
                        }
                      }}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === pageNumber - 1
                          ? `${APP_COLORS.indigoSoftBackground} text-indigo-700`
                          : 'text-text-secondary hover:bg-background'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  )
                })}
                {pageCount > 5 && currentPage < pageCount - 3 && (
                  <span className="px-2 text-text-secondary">...</span>
                )}
                {pageCount > 5 && currentPage < pageCount - 3 && (
                  <button
                    onClick={() => {
                      if (pagination) {
                        pagination.onPageChange(pageCount - 1)
                      }
                    }}
                    className={`px-3 py-1   rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageCount - 1
                        ? `${APP_COLORS.indigoSoftBackground} text-indigo-700`
                        : 'text-text-secondary hover:bg-background'
                    }`}
                  >
                    {pageCount}
                  </button>
                )}
              </div>
              <button
                onClick={() => {
                  if (pagination && currentPage < pageCount - 1) {
                    pagination.onPageChange(currentPage + 1)
                  }
                }}
                disabled={currentPage >= pageCount - 1}
                className="p-2 cursor-pointer rounded-lg border border-border text-text-primary hover:bg-background disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Next page"
              >
                <ChevronRightIcon className="w-5 h-5 " />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

